import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, LogOut, Film, Inbox, X, Star, Play } from "lucide-react";
import {
  isAuthed, verifySession, login as apiLogin, clearToken, formatError,
  fetchAssociations, createAssociation, updateAssociation, deleteAssociation, fetchEnquiries,
} from "@/lib/api";

const inputCls = "w-full bg-void border border-white/15 px-4 py-3 text-offwhite placeholder-white/30 focus:border-gold focus:outline-none transition-colors text-sm";
const emptyForm = { title: "", brand: "", film: "", year: "", description: "", videoUrl: "", thumbnail: "", featured: false };

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await apiLogin(email, password); toast.success("Welcome back."); onSuccess(); }
    catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center container-x pt-24 pb-16">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md border border-white/10 bg-surface/40 p-8 md:p-10">
        <p className="text-xs tracking-[0.35em] uppercase font-bold text-gold mb-3">Admin Access</p>
        <h1 className="font-display text-5xl uppercase tracking-tight text-offwhite mb-8">Sign In</h1>
        <label className="text-xs uppercase tracking-[0.2em] text-white/40">Email</label>
        <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mt-2 mb-5`} placeholder="admin@..." />
        <label className="text-xs uppercase tracking-[0.2em] text-white/40">Password</label>
        <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} mt-2 mb-8`} placeholder="••••••••" />
        <button data-testid="admin-login-submit" disabled={loading} className="w-full bg-gold text-void py-4 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-champagne transition-colors disabled:opacity-70 flex items-center justify-center gap-2">
          {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function AssociationForm({ initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(initial ? { ...emptyForm, ...initial } : emptyForm);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.thumbnail) { toast.error("Title and thumbnail image are required."); return; }
    setSaving(true);
    const payload = { title: form.title, brand: form.brand, film: form.film, year: form.year, description: form.description, videoUrl: form.videoUrl, thumbnail: form.thumbnail, featured: !!form.featured };
    try {
      if (editing) await updateAssociation(initial.id, payload); else await createAssociation(payload);
      toast.success(editing ? "Association updated." : "Association added.");
      onSaved(); onClose();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-void/90 backdrop-blur-md flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="association-form" className="w-full max-w-2xl border border-white/10 bg-surface my-10 p-8">
        <div className="flex items-center justify-between mb-8">
          <h3 className="font-display text-3xl uppercase text-offwhite">{editing ? "Edit Association" : "Add Association"}</h3>
          <button type="button" onClick={onClose} data-testid="association-form-close" className="text-white/60 hover:text-gold"><X size={24} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Title *</label>
            <input data-testid="af-title" value={form.title} onChange={set("title")} className={`${inputCls} mt-2`} placeholder="Brand × Film" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Brand</label>
            <input data-testid="af-brand" value={form.brand} onChange={set("brand")} className={`${inputCls} mt-2`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Film</label>
            <input data-testid="af-film" value={form.film} onChange={set("film")} className={`${inputCls} mt-2`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Year</label>
            <input data-testid="af-year" value={form.year} onChange={set("year")} className={`${inputCls} mt-2`} placeholder="2026" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Featured</label>
            <label className="mt-2 flex items-center gap-3 border border-white/15 px-4 py-3 cursor-pointer">
              <input data-testid="af-featured" type="checkbox" checked={!!form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} className="accent-gold w-4 h-4" />
              <span className="text-sm text-white/70">Mark as featured</span>
            </label>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Video Link (YouTube / Google Drive / .mp4)</label>
            <input data-testid="af-video" value={form.videoUrl} onChange={set("videoUrl")} className={`${inputCls} mt-2`} placeholder="https://youtu.be/… or https://drive.google.com/file/d/…/view" />
            <p className="mt-1 text-xs text-white/30">Paste a YouTube link, a Google Drive share link (set sharing to “anyone with link”), or a direct video URL.</p>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Thumbnail Image URL *</label>
            <input data-testid="af-thumbnail" value={form.thumbnail} onChange={set("thumbnail")} className={`${inputCls} mt-2`} placeholder="https://…" />
            {form.thumbnail && <img src={form.thumbnail} alt="preview" className="mt-3 h-32 w-full object-cover border border-white/10" />}
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Description</label>
            <textarea data-testid="af-description" value={form.description} onChange={set("description")} rows={3} className={`${inputCls} mt-2 resize-none`} />
          </div>
        </div>
        <div className="mt-8 flex gap-3">
          <button data-testid="af-save" disabled={saving} className="bg-gold text-void px-8 py-4 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-champagne transition-colors disabled:opacity-70 flex items-center gap-2">
            {saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : "Save"}
          </button>
          <button type="button" onClick={onClose} className="border border-white/20 text-white/70 px-8 py-4 uppercase tracking-[0.2em] text-xs hover:border-gold hover:text-gold transition-colors">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("associations");
  const [items, setItems] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, e] = await Promise.all([fetchAssociations(), fetchEnquiries().catch(() => [])]);
      setItems(a); setEnquiries(e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try { await deleteAssociation(id); toast.success("Deleted."); setItems((xs) => xs.filter((x) => x.id !== id)); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="pt-28 md:pt-32 pb-24 container-x" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <p className="text-xs tracking-[0.35em] uppercase font-bold text-gold mb-2">Admin</p>
          <h1 className="font-display text-5xl md:text-6xl uppercase tracking-tight text-offwhite">Dashboard</h1>
        </div>
        <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 border border-white/20 text-white/70 px-6 py-3 text-xs uppercase tracking-[0.2em] hover:border-gold hover:text-gold transition-colors">
          <LogOut size={15} /> Log Out
        </button>
      </div>

      <div className="flex gap-2 mb-10 border-b border-white/10">
        {[["associations", "Movie Associations", Film], ["enquiries", "Enquiries", Inbox]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
            className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${tab === key ? "border-gold text-gold" : "border-transparent text-white/50 hover:text-offwhite"}`}>
            <Icon size={15} /> {label} {key === "enquiries" && enquiries.length > 0 && <span className="ml-1 bg-gold text-void px-1.5 rounded-full text-[0.6rem]">{enquiries.length}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-white/50"><Loader2 className="animate-spin" size={18} /> Loading…</div>
      ) : tab === "associations" ? (
        <>
          <div className="flex justify-between items-center mb-6">
            <p className="text-white/50 text-sm">{items.length} associations</p>
            <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-association-btn" className="inline-flex items-center gap-2 bg-gold text-void px-6 py-3 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-champagne transition-colors">
              <Plus size={16} /> Add Association
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((p) => (
              <div key={p.id} data-testid={`admin-association-${p.id}`} className="border border-white/10 bg-surface/40">
                <div className="relative aspect-video overflow-hidden">
                  <img src={p.thumbnail} alt={p.title} className="w-full h-full object-cover" />
                  {p.featured && <span className="absolute top-3 left-3 flex items-center gap-1 bg-gold text-void text-[0.6rem] uppercase tracking-widest px-2 py-1"><Star size={10} fill="currentColor" /> Featured</span>}
                  {p.videoUrl && <span className="absolute bottom-3 right-3 flex items-center gap-1 bg-void/70 text-gold text-[0.6rem] uppercase tracking-widest px-2 py-1"><Play size={10} fill="currentColor" /> Video</span>}
                </div>
                <div className="p-5">
                  <p className="text-[0.6rem] uppercase tracking-[0.2em] text-gold mb-1">{p.brand}{p.year ? ` · ${p.year}` : ""}</p>
                  <h3 className="font-display text-2xl uppercase text-offwhite leading-none">{p.title}</h3>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => { setEditing(p); setShowForm(true); }} data-testid={`edit-association-${p.id}`} className="flex items-center gap-1.5 border border-white/15 text-white/70 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors">
                      <Pencil size={13} /> Edit
                    </button>
                    <button onClick={() => remove(p.id, p.title)} data-testid={`delete-association-${p.id}`} className="flex items-center gap-1.5 border border-white/15 text-white/70 px-4 py-2 text-xs uppercase tracking-widest hover:border-cinered hover:text-cinered transition-colors">
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="space-y-4">
          {enquiries.length === 0 ? (
            <p className="text-white/40">No enquiries yet.</p>
          ) : enquiries.map((e) => (
            <div key={e.id} data-testid={`admin-enquiry-${e.id}`} className="border border-white/10 p-6 bg-surface/40">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-display text-2xl text-offwhite uppercase">{e.full_name} <span className="text-white/40 text-base normal-case">· {e.company || "—"}</span></h3>
                <span className="text-xs uppercase tracking-widest text-gold border border-gold/40 px-3 py-1">{e.service}</span>
              </div>
              <p className="mt-2 text-sm text-white/60">{e.email} · {e.phone || "no phone"}</p>
              <p className="mt-3 text-white/70 font-light">{e.description}</p>
              <p className="mt-1 text-xs text-white/30">{new Date(e.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {showForm && <AssociationForm initial={editing} onClose={() => setShowForm(false)} onSaved={load} />}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    if (!isAuthed()) { setChecking(false); return; }
    verifySession().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); }).finally(() => setChecking(false));
  }, []);
  const logout = () => { clearToken(); setAuthed(false); toast.success("Logged out."); };
  if (checking) return <div className="pt-40 pb-40 text-center text-white/50">Loading…</div>;
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;
  return <Dashboard onLogout={logout} />;
}
